import React, { useEffect, useState } from 'react';
import { Input, Button, Spin, message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../../api/auth';
import { setAuth } from '../../utils/auth';
import { L } from '../../styles/theme';
import AuthShell, { authLabelStyle } from './AuthShell';

const MIN_LEN = 8;

/** Ссылка из письма: приглашение (/accept-invite) и сброс (/reset-password). */
const SetPasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [info, setInfo] = useState<{ email: string; full_name: string | null; kind: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('В ссылке нет кода. Откройте ссылку из письма целиком.');
      setChecking(false);
      return;
    }
    authApi.resetInfo(token)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(e.response?.data?.detail || 'Ссылка недействительна или устарела'))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async () => {
    if (password.length < MIN_LEN) {
      return message.warning(`Пароль должен быть не короче ${MIN_LEN} символов`);
    }
    if (password !== repeat) {
      return message.warning('Пароли не совпадают');
    }
    setSaving(true);
    try {
      const { data } = await authApi.resetPassword(token, password);
      setAuth(data.access_token, data.user);
      message.success('Пароль сохранён');
      navigate('/dashboard');
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Не удалось сохранить пароль');
    } finally {
      setSaving(false);
    }
  };

  if (checking) {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin />
        </div>
      </AuthShell>
    );
  }

  if (error) {
    return (
      <AuthShell>
        <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 6, color: L.ink }}>
          Ссылка не работает
        </div>
        <div style={{ fontSize: 12.5, color: L.muted, lineHeight: 1.6 }}>
          {error} Запросите новую ссылку на странице входа или попросите администратора выслать
          приглашение заново.
        </div>
        <Button block style={{ marginTop: 14 }} onClick={() => navigate('/login')}>
          На страницу входа
        </Button>
      </AuthShell>
    );
  }

  const isInvite = info?.kind === 'invite';

  return (
    <AuthShell footer="После сохранения вы сразу войдёте в панель.">
      <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 4, color: L.ink }}>
        {isInvite ? 'Добро пожаловать' : 'Новый пароль'}
      </div>
      <div style={{ fontSize: 12, color: L.muted, marginBottom: 14, lineHeight: 1.5 }}>
        {isInvite
          ? 'Придумайте пароль — дальше входите по своей почте.'
          : 'Придумайте новый пароль для входа в панель.'}
        <br />
        Почта: <b style={{ color: L.text }}>{info?.email}</b>
      </div>

      <span style={authLabelStyle}>Пароль (не короче {MIN_LEN} символов)</span>
      <Input.Password
        placeholder="••••••••"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoFocus
        autoComplete="new-password"
      />

      <span style={{ ...authLabelStyle, marginTop: 12 }}>Повторите пароль</span>
      <Input.Password
        placeholder="••••••••"
        value={repeat}
        onChange={(e) => setRepeat(e.target.value)}
        onPressEnter={submit}
        autoComplete="new-password"
      />

      <Button type="primary" block loading={saving} onClick={submit} style={{ marginTop: 14 }}>
        Сохранить и войти
      </Button>
    </AuthShell>
  );
};

export default SetPasswordPage;
